import { HttpProvider } from "../http/http.provider";
import { addProvider } from "../ServiceUtil/providerDescriptors";
import { IProviderDescriptor } from "../ServiceUtil/provider.descriptor.interface";
import { ServiceEndpointConstants } from "../ServiceUtil/consts/index";
import { Observable } from "rxjs";
import { share, map, catchError } from "rxjs/operators";
import { of } from "rxjs";

/**
 * Delegate for lightweight auth status API calls.
 * These endpoints are much lighter than the deprecated Resume API.
 * 
 * Enhanced error handling for 401/403 responses (no more 302 redirects).
 */
export class AuthStatusDelegate
{
    constructor(private http: HttpProvider) { }

    /**
     * Lightweight auth status check with auto session extension.
     * Much lighter than Resume API - uses server-side caching.
     * 
     * Enhanced to handle different response scenarios:
     * - 200 OK: Valid session (may be auto-refreshed)
     * - 401 Unauthorized: Invalid/expired session (JSON response)
     * - Network errors: Connection issues
     */
    public getStatus(): Observable<IAuthStatusResponse>
    {
        return this.http.get(ServiceEndpointConstants.endpoints.AUTH_STATUS.STATUS).pipe(
            map((response: any) =>
            {
                // Success response
                return {
                    isAuthenticated: response.data.isAuthenticated || false,
                    userId: response.data.userId,
                    userName: response.data.userName,
                    expiresAt: response.data.expiresAt,
                    sessionExtended: response.data.sessionExtended || false
                } as IAuthStatusResponse;
            }),
            catchError((error: any) =>
            {
                // Handle different error scenarios
                if (error.status === 401)
                {
                    return of({
                        isAuthenticated: false,
                        reason: 'unauthorized'
                    } as IAuthStatusResponse);
                } else if (error.status === 404)
                {
                    // Endpoint doesn't exist yet
                    throw error; // Let the caller handle fallback
                } else
                {
                    return of({
                        isAuthenticated: false,
                        reason: 'error'
                    } as IAuthStatusResponse);
                }
            }),
            share()
        );
    }

    /**
     * Get user profile data. Server caches this data to reduce DB queries.
     * 
     * Enhanced error handling for better UX.
     */
    public getProfile(): Observable<IAuthProfileResponse>
    {
        return this.http.get(ServiceEndpointConstants.endpoints.AUTH_STATUS.PROFILE).pipe(
            map((response: any) =>
            {
                return {
                    success: response.data.success || false,
                    userDetail: response.data.userDetail,
                    message: response.data.message
                } as IAuthProfileResponse;
            }),
            catchError((error: any) =>
            {
                if (error.status === 401)
                {
                    return of({
                        success: false,
                        reason: 'not_authenticated',
                        message: 'User not authenticated'
                    } as IAuthProfileResponse);
                } else if (error.status === 404)
                {
                    // Endpoint doesn't exist yet
                    throw error; // Let the caller handle fallback
                } else
                {
                    return of({
                        success: false,
                        reason: 'error',
                        message: 'Profile fetch failed'
                    } as IAuthProfileResponse);
                }
            }),
            share()
        );
    }

    /**
     * Ultra-lightweight token validation. Only validates JWT, no DB queries.
     */
    public validate(): Observable<IAuthValidateResponse>
    {
        return this.http.get(ServiceEndpointConstants.endpoints.AUTH_STATUS.VALIDATE).pipe(
            map((response: any) =>
            {
                return {
                    isValid: response.data.isValid || false,
                    expiresAt: response.data.expiresAt
                } as IAuthValidateResponse;
            }),
            catchError((error: any) =>
            {
                if (error.status === 404)
                {
                    // Endpoint doesn't exist yet
                    throw error;
                } else
                {
                    return of({
                        isValid: false
                    } as IAuthValidateResponse);
                }
            }),
            share()
        );
    }
}

export interface IAuthStatusResponse
{
    isAuthenticated: boolean;
    userId?: string;
    userName?: string;
    expiresAt?: string;
    sessionExtended?: boolean;
    reason?: 'unauthorized' | 'error' | 'network_error' | 'logged_out';
}

export interface IAuthProfileResponse
{
    success: boolean;
    userDetail?: any;
    message?: string;
    reason?: 'not_authenticated' | 'error' | 'network_error';
}

export interface IAuthValidateResponse
{
    isValid: boolean;
    expiresAt?: string;
}
